'use client'

import { useCallback, useEffect, useRef, useState } from 'react'
import type {
    ICompanyDomain,
    ICompanyDomainRefreshResult,
} from '@/api/organization/company-domain/get-company-domain-query'
import { useRemoveCompanyDomainMutation } from '@/api/organization/company-domain/remove-company-domain-mutation'
import { useTurnOffCompanyDomainSuggestionsMutation } from '@/api/organization/company-domain/turn-off-company-domain-suggestions-mutation'
import { toast } from '@/features/toast/toast'
import { getSuccessfulCompanyDomainRefreshData } from '@/features/settings-modal/tabs/organization/utils/claim-company-domain'
import { isPreparedCompanyDomainOrganizationActive } from '@/features/settings-modal/tabs/organization/utils/company-domain-claim'
import {
    applyConfirmedCompanyDomainManagement,
    getCompanyDomainManagementFailureMessage,
    getFreshCompanyDomainManagementTarget,
    hasAuthoritativeCompanyDomainManagementOutcome,
    type ICompanyDomainManagementAction,
    type IConfirmedCompanyDomainManagement,
    type IPreparedCompanyDomainManagementAction,
} from '@/features/settings-modal/tabs/organization/utils/company-domain-management'

interface IUseCompanyDomainManagementParams {
    activeOrganizationId: string | undefined
    companyDomain: ICompanyDomain
    refreshCompanyDomain: () => Promise<ICompanyDomainRefreshResult>
    canManage: boolean
    isClaimSubmitting: boolean
    onPreparationStarted: () => void
    onPreparationFailed: (message: string) => void
    onConfirmed: () => void
}

/** Coordinates exact-domain preparation, mutation, and Clerk-backed confirmation. */
export function useCompanyDomainManagement({
    activeOrganizationId,
    companyDomain,
    refreshCompanyDomain,
    canManage,
    isClaimSubmitting,
    onPreparationStarted,
    onPreparationFailed,
    onConfirmed,
}: IUseCompanyDomainManagementParams) {
    const turnOffSuggestionsMutation = useTurnOffCompanyDomainSuggestionsMutation()
    const removeDomainMutation = useRemoveCompanyDomainMutation()
    const activeOrganizationIdRef = useRef(activeOrganizationId)
    activeOrganizationIdRef.current = activeOrganizationId
    const confirmationVersionRef = useRef(0)
    const [preparedAction, setPreparedAction] = useState<IPreparedCompanyDomainManagementAction | null>(null)
    const [confirmedManagement, setConfirmedManagement] = useState<IConfirmedCompanyDomainManagement | null>(null)
    const [errorMessage, setErrorMessage] = useState<string | null>(null)
    const [preparingActionKey, setPreparingActionKey] = useState<string | null>(null)

    const displayDomains = applyConfirmedCompanyDomainManagement(
        companyDomain.domains,
        activeOrganizationId,
        confirmedManagement
    )
    const isMutationPending = turnOffSuggestionsMutation.isPending || removeDomainMutation.isPending
    const isBusy = preparingActionKey !== null || isMutationPending || confirmedManagement !== null

    const resetManagementState = useCallback(() => {
        confirmationVersionRef.current += 1
        setPreparedAction(null)
        setConfirmedManagement(null)
        setErrorMessage(null)
        setPreparingActionKey(null)
    }, [])

    useEffect(() => {
        resetManagementState()
    }, [activeOrganizationId, resetManagementState])

    useEffect(() => {
        if (confirmedManagement && hasAuthoritativeCompanyDomainManagementOutcome(companyDomain, confirmedManagement)) {
            setConfirmedManagement(null)
        }
    }, [companyDomain, confirmedManagement])

    const prepareAction = async (action: ICompanyDomainManagementAction, domain: ICompanyDomain['domains'][number]) => {
        const preparedOrganizationId = activeOrganizationId
        if (!canManage || !preparedOrganizationId || isClaimSubmitting || isBusy) return

        const prepared: IPreparedCompanyDomainManagementAction = {
            action,
            organizationId: preparedOrganizationId,
            domainId: domain.id,
            domainName: domain.name,
            enrollmentMode: domain.enrollmentMode,
        }
        const actionKey = `${action}:${domain.id}`
        setPreparingActionKey(actionKey)
        onPreparationStarted()
        setErrorMessage(null)

        try {
            const refreshed = await refreshCompanyDomain().catch(() => null)
            if (!isPreparedCompanyDomainOrganizationActive(activeOrganizationIdRef.current, preparedOrganizationId)) {
                toast.error('Your active workspace changed. Open Company domain in the intended workspace again.')
                return
            }

            const refreshedData = refreshed ? getSuccessfulCompanyDomainRefreshData(refreshed) : undefined
            const freshDomain = getFreshCompanyDomainManagementTarget(refreshedData, prepared)
            if (!freshDomain) {
                onPreparationFailed(
                    refreshedData
                        ? 'This domain or its available actions changed in Clerk. Refresh the status and try again.'
                        : "We couldn't refresh the company domain status. Try again before changing this domain."
                )
                return
            }

            setPreparedAction({
                ...prepared,
                domainName: freshDomain.name,
                enrollmentMode: freshDomain.enrollmentMode,
            })
        } finally {
            setPreparingActionKey((currentKey) => (currentKey === actionKey ? null : currentKey))
        }
    }

    const markConfirmed = (prepared: IPreparedCompanyDomainManagementAction) => {
        const message =
            prepared.action === 'remove_domain'
                ? `${prepared.domainName} removed from Claimed Domains.`
                : prepared.enrollmentMode === 'automatic_invitation'
                  ? `Automatic invitations turned off for ${prepared.domainName}.`
                  : `Suggestions turned off for ${prepared.domainName}.`

        confirmationVersionRef.current += 1
        setConfirmedManagement(prepared)
        onConfirmed()
        setErrorMessage(null)
        toast.success(message)
    }

    const readBackOutcome = async (prepared: IPreparedCompanyDomainManagementAction) => {
        const refreshed = await refreshCompanyDomain().catch(() => null)
        if (!isPreparedCompanyDomainOrganizationActive(activeOrganizationIdRef.current, prepared.organizationId)) {
            toast.error('Your active workspace changed. Open Company domain in the intended workspace again.')
            return 'workspace_changed' as const
        }

        const refreshedData = refreshed ? getSuccessfulCompanyDomainRefreshData(refreshed) : undefined
        if (!refreshedData) return 'unconfirmed' as const
        return hasAuthoritativeCompanyDomainManagementOutcome(refreshedData, prepared)
            ? ('confirmed' as const)
            : ('superseded' as const)
    }

    const confirmAction = async () => {
        const prepared = preparedAction
        if (!prepared) return
        if (
            !canManage ||
            !isPreparedCompanyDomainOrganizationActive(activeOrganizationIdRef.current, prepared.organizationId)
        ) {
            setErrorMessage(
                'Your Company domain permissions or active workspace changed. Refresh the status before trying again.'
            )
            throw new Error('Company domain management context changed')
        }

        setErrorMessage(null)
        let result
        try {
            result =
                prepared.action === 'remove_domain'
                    ? await removeDomainMutation.mutateAsync({
                          organizationId: prepared.organizationId,
                          domainId: prepared.domainId,
                      })
                    : await turnOffSuggestionsMutation.mutateAsync({
                          organizationId: prepared.organizationId,
                          domainId: prepared.domainId,
                      })
        } catch {
            const readBack = await readBackOutcome(prepared)
            if (readBack === 'confirmed') {
                markConfirmed(prepared)
                return
            }
            if (readBack === 'workspace_changed') throw new Error('Active workspace changed')
            if (readBack === 'superseded') {
                setConfirmedManagement(null)
            }

            setErrorMessage("We couldn't confirm the change in Clerk. Refresh the status and try again.")
            throw new Error('Company domain management was not confirmed')
        }

        if (!isPreparedCompanyDomainOrganizationActive(activeOrganizationIdRef.current, prepared.organizationId)) {
            toast.error('Your active workspace changed. Open Company domain in the intended workspace again.')
            throw new Error('Active workspace changed')
        }

        if (result.outcome === 'success') {
            markConfirmed(prepared)
            await refreshCompanyDomain().catch(() => null)
            return
        }

        const readBack = await readBackOutcome(prepared)
        if (readBack === 'confirmed') {
            markConfirmed(prepared)
            return
        }
        if (readBack === 'workspace_changed') throw new Error('Active workspace changed')
        if (readBack === 'superseded') {
            setConfirmedManagement(null)
        }

        setErrorMessage(getCompanyDomainManagementFailureMessage(result))
        throw new Error('Company domain management failed')
    }

    const closeConfirmation = () => {
        setPreparedAction(null)
        setErrorMessage(null)
    }

    return {
        displayDomains,
        preparedAction,
        errorMessage,
        isMutationPending,
        isBusy,
        prepareAction,
        confirmAction,
        closeConfirmation,
        resetManagementState,
    }
}
